// Function to capitalize first letter of each word
export const capitalizeWords = (str: string) => {
  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

// Scrolls the window to the top with smooth behavior
export const scrollToTop = (): void => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

/**
 * Validation utilities for form inputs
 */

/**
 * Email validation regex pattern
 * Matches standard email format: local@domain.tld
 * - Requires at least 2 characters in the TLD (e.g., .com, .co.uk)
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Validates an email address
 * @param email - The email address to validate
 * @returns true if the email is valid, false otherwise
 */
export const isValidEmail = (email: string): boolean => {
  const trimmedEmail = email.trim();
  return trimmedEmail.length > 0 && EMAIL_REGEX.test(trimmedEmail);
};

/**
 * Validates an email address and returns an error message if invalid
 * @param email - The email address to validate
 * @returns Error message if invalid, undefined if valid
 */
export const validateEmail = (email: string): string | undefined => {
  const trimmedEmail = email.trim();
  if (!trimmedEmail) {
    return 'Email is required';
  }
  if (!EMAIL_REGEX.test(trimmedEmail)) {
    return 'Please enter a valid email address';
  }
  if (trimmedEmail.length > 255) {
    return 'Email must be less than 255 characters';
  }
  return undefined;
};

/**
 * Creates a URL-friendly slug from text
 * Converts to lowercase, replaces spaces/special chars with hyphens
 */
export const createSlug = (text: string): string => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Creates a category slug from category name
 * @param categoryName - The category name (e.g., "Polishing & Finishing")
 * @returns URL-friendly slug (e.g., "polishing-finishing")
 */
export const createCategorySlug = (categoryName: string): string => {
  return createSlug(categoryName);
};

/**
 * Creates a product URL slug from variant ID and description
 * Format: {variantId}-{slug}
 * Example: "15718-rodin-soft-reline-kit-rd-purpose"
 */
export const createProductSlug = (variantId: number, variantDescription: string): string => {
  const descriptionSlug = createSlug(variantDescription);
  return `${variantId}-${descriptionSlug}`;
};

/**
 * Extracts variantId from a product slug
 * Returns null if the slug format is invalid
 * @param slug - The product slug (format: "{variantId}-{description-slug}")
 * @returns The variantId as a number, or null if invalid
 */
export const extractVariantIdFromSlug = (slug: string): number | null => {
  const match = slug.match(/^(\d+)-/);
  if (match) {
    const variantId = parseInt(match[1], 10);
    return isNaN(variantId) ? null : variantId;
  }
  return null;
};

/**
 * Finds a category by matching its slug to a category name
 * @param slug - The URL slug (e.g., "crown-bridge")
 * @param categories - Array of categories to search
 * @returns The matching category, or undefined if not found
 */
export const findCategoryBySlug = <T extends { name: string }>(
  slug: string,
  categories: T[]
): T | undefined => {
  return categories.find(
    (category) => createSlug(category.name) === slug.toLowerCase()
  );
};

/**
 * Currency formatting utility
 * Formats prices according to currency code (GBP, EUR, etc.)
 */

export type CurrencyCode = 'GBP' | 'EUR';

/**
 * Formats a number with currency symbol prefix
 * Uses locale-specific formatting:
 * - GBP: UK-style (comma for thousands, period for decimals) → £1,234.56
 * - EUR: € before number (en-IE) → €1,234.56
 * @param amount - The numeric amount to format
 * @param currencySymbol - The currency symbol (£, €, etc.)
 * @param currencyCode - The currency code (GBP, EUR) - determines the formatting style
 * @returns Formatted string with currency symbol (e.g., "£1,234.56" or "€1,234.56")
 */
export const formatPrice = (
  amount: number,
  currencySymbol: string,
  currencyCode: CurrencyCode = 'GBP'
): string => {
  // Use appropriate locale based on currency code
  // GBP: UK-style formatting (comma for thousands, period for decimals) → £1,234.56
  // EUR: en-IE places € before the number → €1,234.56
  const locale = currencyCode === 'EUR' ? 'en-IE' : 'en-GB';

  // Format the magnitude and place any minus sign before the symbol, so a
  // negative reads "-£1,234.56" rather than "£-1,234.56".
  const formattedNumber = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(amount));

  // Return with currency symbol prefix
  return `${amount < 0 ? '-' : ''}${currencySymbol}${formattedNumber}`;
};

/**
 * Formats a number as currency using Intl.NumberFormat
 * Uses locale-specific formatting based on currency code
 * @param amount - The numeric amount to format
 * @param currencyCode - The currency code (GBP, EUR, etc.)
 * @param options - Optional Intl.NumberFormat options
 * @returns Formatted currency string (e.g., "£1,234.56" or "€1,234.56")
 */
export const formatCurrency = (
  amount: number,
  currencyCode: CurrencyCode,
  options?: Intl.NumberFormatOptions
): string => {
  const defaultOptions: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...options,
  };

  // Use appropriate locale based on currency code
  // GBP: UK-style formatting (comma for thousands, period for decimals)
  // EUR: en-IE places € before the number
  const locale = currencyCode === 'EUR' ? 'en-IE' : 'en-GB';
  return new Intl.NumberFormat(locale, defaultOptions).format(amount);
};

/**
 * Formats a date string for display
 * @param dateString - The date string to format
 * @param locale - Optional locale (defaults to 'en-GB')
 * @param options - Optional Intl.DateTimeFormat options
 * @returns Formatted date string (e.g., "20 Jan 2025")
 */
export const formatDate = (
  dateString: string,
  locale: string = 'en-GB',
  options?: Intl.DateTimeFormatOptions
): string => {
  const date = new Date(dateString);
  const defaultOptions: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...options,
  };
  return date.toLocaleDateString(locale, defaultOptions);
};
